import React from 'react';
import { useTranslation } from 'react-i18next';
import { EMAIL_CAL_GROUP, lighten } from '../tabPalette';

// ── Shared "email activity" badge (owner 2026-08-30) ────────────────────────────────────────────
// Extracted from Peak's local SentBadge, which only Quotation / Invoice / Receipt could use. The
// same ✉N marker is wanted wherever a record carries correspondence ("我们的系统有大量的地方都需要
// 显示发邮件的图标实际收发邮件的数量"), so it lives here and every list renders the identical thing.
//
// Two shapes, one component:
//   · count only            — how many times WE emailed the document (Peak's original meaning).
//   · count + inbound       — a whole conversation: total messages, of which `inbound` came back.
//     `unread` turns the badge amber and appends a dot, so a reply nobody has read stands out.
// Rendering nothing when there is no traffic is deliberate: an empty row must stay quiet.
//
// Settled, it wears the top menu's Email / Contacts / Calendar purple (report de340118, 2026-10-04,
// "Email Notifications": "Change system email notifications … to the purple theme color"):
// EMAIL_CAL_GROUP.color #7F77DD for the icon and number, the box's see-through wash laid on white
// first, #F3F2FC, for the fill — as Tracking's Email chain button does — so the badge looks the same
// on a cream, grey or blue row. Taken from the palette, so it follows the box. The purple reads about
// 3.4:1 on it, a little more than the green did. Was green, #1D9E75 on #E7F6EE. The amber state and
// the red unread dot are as they were.
// Edged since de340118's 2nd opening ("add the dark purple border around the bubble"): a 1px line
// in the icon and number's own purple, EMAIL_CAL_GROUP.color. The amber state draws a see-through
// line of the same width, so the line never makes one state bigger than the other.
// The line adds 2px each way — by arithmetic, not measured: a two-digit badge about 45px wide, inside
// Peak's 48px ACT_MAIL_W slot, and about 21px high, under the 26px ⋮ that sets Peak's ROW_H.
// `style` is laid over the badge's own, last. Tracking passes the 10px the same opening asked for
// between the client's name (or the not-notified tag after it) and the badge; they touched, as JSX
// drops the line breaks between the three. Peak passes nothing — there the badge starts its own slot.
// Amber only for an unread reply since de340118's 3rd opening ("Still many yellow email
// notifications when they should all be the purple color for (Quotations/Invoices/Receipts/
// Approvals)"). Peak's original rule, that a document emailed two or more times turns amber, had
// survived both earlier openings, so Quotation / Invoice / Receipt still showed amber ✉2, ✉7 and so
// on. It is gone: a count-only badge is purple whatever its number. Only Tracking passes `unread`,
// so an unopened reply there is the one amber badge left, red dot and all — the report did not name
// Tracking. The Approvals page draws no ✉N badge; its amber "Awaiting approval" is a status pill.

const fmtEpoch = (s?: number) => {
  if (!s) return '';
  const d = new Date(s * 1000);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export default function MailBadge({ count, last, inbound, unread, onClick, title, style }: {
  count?: number;          // total messages (or times emailed)
  last?: number;           // epoch seconds of the most recent one
  inbound?: number;        // how many of them came FROM the counterparty
  unread?: number;         // inbound messages nobody has opened yet
  onClick?: () => void;    // opens the thread viewer where the list has one
  title?: string;          // overrides the derived tooltip
  style?: React.CSSProperties;   // laid over the badge's own, last (Tracking's gap after the name)
}) {
  const { t } = useTranslation();
  if (!count || count < 1) return null;
  const hasUnread = !!unread && unread > 0;
  // Amber means "look at me": a reply nobody has opened yet — and only that. Peak's original second
  // rule, a document we have emailed more than once, is gone (de340118's 3rd opening, see above).
  // Purple (see above) is the settled, nothing-to-do state, whatever the count.
  const warn = hasUnread;
  const tip = title
    || (inbound !== undefined
      ? `${count} message(s)${inbound ? ` · ${inbound} received` : ''}${hasUnread ? ` · ${unread} unread` : ''}${last ? ' · ' + fmtEpoch(last) : ''}`
      : (onClick ? t('iv_thread_open') : `${t('iv_sent_n').replace('{n}', String(count))}${last ? ' · ' + fmtEpoch(last) : ''}`));
  return (
    <span title={tip} onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11.5, fontWeight: 700,
        color: warn ? '#B45309' : EMAIL_CAL_GROUP.color, background: warn ? '#FEF3C7' : lighten(EMAIL_CAL_GROUP.tint, 0),
        border: warn ? '1px solid transparent' : `1px solid ${EMAIL_CAL_GROUP.color}`,
        borderRadius: 999, padding: '2px 7px', marginRight: 8, verticalAlign: 'middle',
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}>
      <i className="ti ti-mail" style={{ fontSize: 12 }} aria-hidden />{count}
      {hasUnread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#DC2626', marginLeft: 1 }} aria-hidden />}
    </span>
  );
}
